"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import AuthModal from "@/components/AuthModal";
import {
  clearPostAuthRedirect,
  setPostAuthRedirect,
} from "@/components/PostAuthRedirectGuard";

export type AuthModalSource = "reveal_gatekeeper" | "header" | "checkout" | "default";

interface OpenAuthModalOptions {
  callbackUrl?: string;
  /** After Google popup sign-in, navigate here instead of staying on the current page. */
  postAuthRedirect?: string;
  authSource?: AuthModalSource;
  purchaseContext?: {
    priceId: string;
    credits?: number;
    mode: string;
  };
  onSuccess?: () => void;
}

interface AuthModalContextValue {
  isOpen: boolean;
  /** True from sign-in success until dashboard data is ready. */
  postAuthLoading: boolean;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  closeAuthModal: () => void;
  markPostAuthLoading: () => void;
  clearPostAuthLoading: () => void;
}

const AuthModalContext = createContext<AuthModalContextValue | undefined>(undefined);

/**
 * Global provider that mounts a single {@link AuthModal} and exposes
 * `useAuthModal()` so any component - sidebar, header, paywalls - can
 * pop the modal without duplicating state.
 */
export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [postAuthLoading, setPostAuthLoading] = useState(false);
  const [options, setOptions] = useState<OpenAuthModalOptions>({});

  const openAuthModal = useCallback((opts: OpenAuthModalOptions = {}) => {
    if (opts.postAuthRedirect) {
      setPostAuthRedirect(opts.postAuthRedirect);
    } else {
      clearPostAuthRedirect();
    }
    setOptions(opts);
    setIsOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsOpen(false);
    clearPostAuthRedirect();
  }, []);

  const markPostAuthLoading = useCallback(() => {
    setPostAuthLoading(true);
  }, []);

  const clearPostAuthLoading = useCallback(() => {
    setPostAuthLoading(false);
  }, []);

  useEffect(() => {
    if (!postAuthLoading) return;
    const timer = setTimeout(() => setPostAuthLoading(false), 15000);
    return () => clearTimeout(timer);
  }, [postAuthLoading]);

  const value = useMemo<AuthModalContextValue>(
    () => ({
      isOpen,
      postAuthLoading,
      openAuthModal,
      closeAuthModal,
      markPostAuthLoading,
      clearPostAuthLoading,
    }),
    [isOpen, postAuthLoading, openAuthModal, closeAuthModal, markPostAuthLoading, clearPostAuthLoading]
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal
        isOpen={isOpen}
        onClose={closeAuthModal}
        onSuccess={options.onSuccess}
        callbackUrl={options.callbackUrl}
        postAuthRedirect={options.postAuthRedirect}
        authSource={options.authSource}
        purchaseContext={options.purchaseContext}
        markPostAuthLoading={markPostAuthLoading}
      />
    </AuthModalContext.Provider>
  );
}

export function useAuthModal(): AuthModalContextValue {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error("useAuthModal must be used within an AuthModalProvider");
  }
  return ctx;
}
